import { PARTY_DECLINE_MS, PARTY_INVITE_MS, PARTY_LEAD_GRACE_MS, PARTY_SEAT_MS, PARTY_SIZE, PARTY_TRUCE_MS } from "./constants.ts";
import { EMPTY_STATE, seatOf, type Party, type PartyInvite, type PartyNotice, type PartySeat, type PartyState, type PartyWait, type TruceWrite, type Who } from "./state.ts";
import type { PartyRefusal } from "./words.ts";

/**
 * The party's transitions (ADR-101 decisions 2 to 5 and 8 to 12): pure functions, each from a state to the new state
 * and the notices, truces and waits the change causes. None mutates its input. A refusal changes nothing and names its
 * reason; the gateway writes the result to the store in one transaction and only then sends it.
 */

/**
 * A change that went through: the new state, the notices to send, the truces to write and the waits to record.
 */
export interface Change {
  readonly ok: true;
  readonly state: PartyState;
  readonly notices: readonly PartyNotice[];
  readonly truces: readonly TruceWrite[];
  readonly waits: readonly PartyWait[];
}

/**
 * A request refused: nothing changed.
 */
export interface Refused {
  readonly ok: false;
  readonly reason: PartyRefusal;
  readonly waitMs: number;
}

export type Outcome = Change | Refused;

/**
 * What the gateway knows about an accepting character that the party does not: it is in a party, a seated member's
 * family and its own have blocked each other, it is in combat with a seated member, the wait before the truce cap
 * (`truceWait`) lets the accept go ahead, 0 for none, and, from the map servers (`partyFlagCheck`, ADR-076's amendment of
 * 2026-10-08, E6), its preference and level, the founder's preference (read only when the accept seats the second
 * member) and the rules' `pvp_level`.
 */
export interface AnswerFacts {
  readonly inParty: boolean;
  readonly blocked: boolean;
  readonly combat: boolean;
  readonly truceWaitMs: number;
  readonly preference: number;
  readonly level: number;
  readonly founderPreference: number;
  readonly pvpLevel: number;
}

/**
 * Why invites were withdrawn: their inviter left, was removed, lost the right to invite, or the party ended.
 */
export type WithdrawCause = "left" | "removed" | "revoked" | "ended";

/**
 * A refusal.
 *
 * @param reason The reason.
 * @param waitMs The wait, 0 for none.
 * @returns The refusal.
 */
function refused(reason: PartyRefusal, waitMs = 0): Refused {
  return { ok: false, reason, waitMs };
}

/**
 * A change.
 *
 * @param state The new state.
 * @param notices The notices.
 * @param truces The truces.
 * @param waits The waits.
 * @returns The change.
 */
function changed(state: PartyState, notices: readonly PartyNotice[] = [], truces: readonly TruceWrite[] = [], waits: readonly PartyWait[] = []): Change {
  return { ok: true, state, notices, truces, waits };
}

/**
 * Two changes in a row: the second's state, and both's notices, truces and waits in order.
 *
 * @param first The earlier change.
 * @param second The later change.
 * @returns The joined change.
 */
function then(first: Change, second: Change): Change {
  return changed(second.state, [...first.notices, ...second.notices], [...first.truces, ...second.truces], [...first.waits, ...second.waits]);
}

/**
 * The character ids of seats, in seat order.
 *
 * @param seats The seats.
 * @returns The ids.
 */
function idsOf(seats: readonly PartySeat[]): number[] {
  return seats.map((seat) => seat.character);
}

/**
 * The lowest place no seat holds.
 *
 * @param seats The seats.
 * @returns A place 0 to 5.
 */
function lowestFreePlace(seats: readonly PartySeat[]): number {
  let place = 0;
  while (seats.some((seat) => seat.place === place)) {
    place += 1;
  }
  return place;
}

/**
 * The seat the lead passes to (decision 9): the online member that joined earliest, else the seated member that joined
 * earliest, whatever their places.
 *
 * @param seats The seats that remain, at least one.
 * @returns The successor.
 */
function successor(seats: readonly PartySeat[]): PartySeat {
  const online = seats.filter((seat) => seat.online);
  const pool = online.length > 0 ? online : seats;
  return pool.reduce((best, seat) => (seat.joined < best.joined ? seat : best));
}

/**
 * A party with one seat given the right to invite or not, and another's turned the other way: how the lead moves.
 *
 * @param seats The seats.
 * @param from The character that loses the lead, or 0 for none.
 * @param to The character that takes it.
 * @returns New seats.
 */
function movedLead(seats: readonly PartySeat[], from: number, to: number): PartySeat[] {
  return seats.map((seat) => (seat.character === to ? { ...seat, mayInvite: true } : seat.character === from ? { ...seat, mayInvite: false } : seat));
}

/**
 * A state with a party of one and no invite ended: such a party exists only while it holds a pending invite.
 *
 * @param state The state.
 * @returns The state, or the empty one.
 */
function collapsed(state: PartyState): PartyState {
  return state.party !== null && state.party.seats.length < 2 && state.invites.length === 0 ? EMPTY_STATE : state;
}

/**
 * Withdraws invites (decision 5): those of one inviter, or all of them for `null`. Each invited is sent `withdrawn`,
 * and an inviter that left, or whose party ended, starts the account's wait toward each (decision 4.5); a removal or a
 * revoke starts none.
 *
 * @param state The state.
 * @param inviter The inviting character, or null for every invite.
 * @param cause Why.
 * @param now The time in milliseconds since the epoch.
 * @returns The change.
 */
export function withdraw(state: PartyState, inviter: number | null, cause: WithdrawCause, now: number): Change {
  const gone = state.invites.filter((invite) => inviter === null || invite.inviter === inviter);
  const kept = state.invites.filter((invite) => !gone.includes(invite));
  const notices = gone.map((invite): PartyNotice => ({ kind: "withdrawn", about: invite.inviter, to: [invite.invited], invite: invite.id }));
  const waits = cause === "left" || cause === "ended" ? gone.map((invite): PartyWait => ({ account: invite.inviterAccount, character: invite.invited, until: now + PARTY_DECLINE_MS })) : [];
  return changed({ party: state.party, invites: kept }, notices, [], waits);
}

/**
 * An invite (decision 4, once its checks passed): a character in no party forms a party of one and is its leader at
 * place 0; a seated character needs the right to invite. The invite is pending for `PARTY_INVITE_MS`.
 *
 * @param state The inviter's party state.
 * @param inviter The inviting character.
 * @param invited The invited character's id and account.
 * @param ids The id of a party formed by this invite, and of the invite.
 * @param now The time in milliseconds since the epoch.
 * @returns The change, or `leader` when the inviter is seated without the right or not seated in this party.
 */
export function inviteTo(state: PartyState, inviter: Who, invited: Pick<Who, "character" | "account">, ids: { readonly party: number; readonly invite: number }, now: number): Outcome {
  let party = state.party;
  if (party === null) {
    const founder: PartySeat = { ...inviter, place: 0, joined: 0, online: true, heldUntil: 0, mayInvite: true };
    party = { id: ids.party, version: 1, flag: 0, leader: inviter.character, seats: [founder] };
  } else if (seatOf(party, inviter.character)?.mayInvite !== true) {
    return refused("leader");
  }
  const invite: PartyInvite = { id: ids.invite, party: party.id, inviter: inviter.character, inviterAccount: inviter.account, invited: invited.character, invitedAccount: invited.account, expires: now + PARTY_INVITE_MS };
  return changed({ party, invites: [...state.invites, invite] });
}

/**
 * An answer to an invite (decision 5). The invite must be pending, addressed to the answerer and not lapsed, else
 * `gone`. A decline removes it, tells the inviter and starts the inviter's account's wait. An accept checks, in
 * order, `member`, `full`, `cannot`, `combat` and `recent` (the truce cap), then `flag` (the answerer's preference is not
 * the party's flag, which for a party of one is the founder's preference) and `level` (a Flagged party and an answerer
 * below `pvp_level`), leaving the invite pending on a refusal; on success the answerer takes the lowest free place with
 * no right to invite and every member is sent `joined`. The seat that makes the second sets the party's flag; later
 * seats never change it.
 *
 * @param state The inviting party's state.
 * @param invite The invite's id.
 * @param answerer The answering character.
 * @param accept True to accept.
 * @param facts What the gateway knows of the answerer.
 * @param now The time in milliseconds since the epoch.
 * @returns The change or the refusal.
 */
export function answerInvite(state: PartyState, invite: number, answerer: Who, accept: boolean, facts: AnswerFacts, now: number): Outcome {
  const found = state.invites.find((candidate) => candidate.id === invite);
  const party = state.party;
  if (found === undefined || party === null || found.invited !== answerer.character || found.expires <= now) {
    return refused("gone");
  }
  const remaining = state.invites.filter((candidate) => candidate !== found);
  if (!accept) {
    const notice: PartyNotice = { kind: "declined", about: answerer.character, to: [found.inviter], invite: found.id };
    return changed(collapsed({ party, invites: remaining }), [notice], [], [{ account: found.inviterAccount, character: answerer.character, until: now + PARTY_DECLINE_MS }]);
  }
  if (facts.inParty) {
    return refused("member");
  }
  if (party.seats.length >= PARTY_SIZE) {
    return refused("full");
  }
  if (facts.blocked) {
    return refused("cannot");
  }
  if (facts.combat) {
    return refused("combat");
  }
  if (facts.truceWaitMs > 0) {
    return refused("recent", facts.truceWaitMs);
  }
  const flag = party.seats.length < 2 ? facts.founderPreference : party.flag;
  if (facts.preference !== flag) {
    return refused("flag");
  }
  if (flag === 1 && facts.level < facts.pvpLevel) {
    return refused("level");
  }
  const joined = party.seats.reduce((most, seat) => Math.max(most, seat.joined), 0) + 1;
  const seat: PartySeat = { ...answerer, place: lowestFreePlace(party.seats), joined, online: true, heldUntil: 0, mayInvite: false };
  const seats = [...party.seats, seat];
  const next: Party = { ...party, version: party.version + 1, flag, seats };
  return changed({ party: next, invites: remaining }, [{ kind: "joined", about: answerer.character, to: idsOf(seats), invite: 0 }]);
}

/**
 * Lets the invites that have reached their time lapse (decision 5): each inviter is sent `expired` and its account's
 * wait toward the invited starts; a party of one with no invite left ends.
 *
 * @param state The state.
 * @param now The time in milliseconds since the epoch.
 * @returns The change.
 */
export function expireInvites(state: PartyState, now: number): Outcome {
  const due = state.invites.filter((invite) => invite.expires <= now);
  if (due.length === 0) {
    return changed(state);
  }
  const notices = due.map((invite): PartyNotice => ({ kind: "expired", about: invite.invited, to: [invite.inviter], invite: invite.id }));
  const waits = due.map((invite): PartyWait => ({ account: invite.inviterAccount, character: invite.invited, until: now + PARTY_DECLINE_MS }));
  return changed(collapsed({ party: state.party, invites: state.invites.filter((invite) => !due.includes(invite)) }), notices, [], waits);
}

/**
 * Frees the seat of a character that is seated (decisions 8, 9, 10 and 12): a truce is written between it and each
 * member then seated; the lead passes if it held it; the invites it sent are withdrawn; a party left with fewer than
 * two seats ends and withdraws every invite.
 *
 * @param state The state, holding the character's seat.
 * @param character The parting character.
 * @param kind The notice the parting makes, `left` or `removed`.
 * @param now The time in milliseconds since the epoch.
 * @returns The change.
 */
function part(state: PartyState, character: number, kind: "left" | "removed", now: number): Change {
  const party = state.party as Party;
  const remaining = party.seats.filter((seat) => seat.character !== character);
  const others = idsOf(remaining);
  const truces = others.map((other): TruceWrite => ({ a: character, b: other, until: now + PARTY_TRUCE_MS }));
  const told = kind === "removed" ? [character, ...others] : others;
  if (remaining.length < 2) {
    const notices: PartyNotice[] = remaining.length === 1 ? [{ kind, about: character, to: told, invite: 0 }, { kind: "ended", about: character, to: others, invite: 0 }] : [];
    const ending = withdraw(state, null, "ended", now);
    return changed(EMPTY_STATE, [...notices, ...ending.notices], truces, ending.waits);
  }
  let seats = remaining;
  let leader = party.leader;
  const notices: PartyNotice[] = [{ kind, about: character, to: told, invite: 0 }];
  if (leader === character) {
    leader = successor(remaining).character;
    seats = movedLead(remaining, 0, leader);
    notices.push({ kind: "leader", about: leader, to: others, invite: 0 });
  }
  const moved = changed({ party: { ...party, version: party.version + 1, leader, seats }, invites: state.invites }, notices, truces);
  return then(moved, withdraw(moved.state, character, kind === "removed" ? "removed" : "left", now));
}

/**
 * A member leaves, at once (decision 8).
 *
 * @param state The party's state.
 * @param character The leaving character.
 * @param now The time in milliseconds since the epoch.
 * @returns The change, or `alone` when the character is not seated.
 */
export function leave(state: PartyState, character: number, now: number): Outcome {
  return seatOf(state.party, character) === undefined ? refused("alone") : part(state, character, "left", now);
}

/**
 * The leader removes a seated member (decision 9): the seat is freed as by leaving, the removed one is told with the
 * others, and the invites it sent are withdrawn without a wait.
 *
 * @param state The party's state.
 * @param actor The acting character.
 * @param target The member to remove.
 * @param now The time in milliseconds since the epoch.
 * @returns The change, or `leader` when the actor is not the leader, `absent` when the target is not seated and
 *   `self` when it is the leader.
 */
export function removeMember(state: PartyState, actor: number, target: number, now: number): Outcome {
  const party = state.party;
  if (party === null || party.leader !== actor) {
    return refused("leader");
  }
  if (seatOf(party, target) === undefined) {
    return refused("absent");
  }
  return target === actor ? refused("self") : part(state, target, "removed", now);
}

/**
 * The leader hands the lead to a seated online member (decision 9): the new leader takes the leader's rights, the
 * former leader keeps none and loses the invites it had pending, and every other grant stays.
 *
 * @param state The party's state.
 * @param actor The acting character.
 * @param target The new leader.
 * @param now The time in milliseconds since the epoch.
 * @returns The change, or `leader`, `absent`, `self` or `offline`.
 */
export function promote(state: PartyState, actor: number, target: number, now: number): Outcome {
  const party = state.party;
  if (party === null || party.leader !== actor) {
    return refused("leader");
  }
  const seat = seatOf(party, target);
  if (seat === undefined) {
    return refused("absent");
  }
  if (target === actor) {
    return refused("self");
  }
  if (!seat.online) {
    return refused("offline");
  }
  const moved = changed({ party: { ...party, version: party.version + 1, leader: target, seats: movedLead(party.seats, actor, target) }, invites: state.invites }, [{ kind: "leader", about: target, to: idsOf(party.seats), invite: 0 }]);
  return then(moved, withdraw(moved.state, actor, "revoked", now));
}

/**
 * The leader gives or takes back a member's right to invite (decision 3). A revoke withdraws the member's pending
 * invites; a grant that changes nothing changes nothing.
 *
 * @param state The party's state.
 * @param actor The acting character.
 * @param target The member.
 * @param value True to give the right.
 * @param now The time in milliseconds since the epoch.
 * @returns The change, or `leader`, `absent` or `self`.
 */
export function grant(state: PartyState, actor: number, target: number, value: boolean, now: number): Outcome {
  const party = state.party;
  if (party === null || party.leader !== actor) {
    return refused("leader");
  }
  const seat = seatOf(party, target);
  if (seat === undefined) {
    return refused("absent");
  }
  if (target === actor) {
    return refused("self");
  }
  if (seat.mayInvite === value) {
    return changed(state);
  }
  const seats = party.seats.map((candidate) => (candidate === seat ? { ...candidate, mayInvite: value } : candidate));
  const next = changed({ party: { ...party, version: party.version + 1, seats }, invites: state.invites }, [{ kind: value ? "granted" : "revoked", about: target, to: idsOf(seats), invite: 0 }]);
  return value ? next : then(next, withdraw(next.state, target, "revoked", now));
}

/**
 * A member's connection closed (decision 12): its seat is kept offline for `PARTY_SEAT_MS` with an empty map and the
 * others are told. A leader keeps the lead, its rights and its pending invites for `PARTY_LEAD_GRACE_MS`, so a blip the
 * page reconnects through changes nothing but the Offline mark; `passLead` moves it once that time has passed.
 *
 * @param state The party's state.
 * @param character The disconnecting character.
 * @param now The time in milliseconds since the epoch.
 * @returns The change; nothing changes for a character not seated or already offline.
 */
export function disconnect(state: PartyState, character: number, now: number): Outcome {
  const party = state.party;
  const seat = seatOf(party, character);
  if (party === null || seat === undefined || !seat.online) {
    return changed(state);
  }
  const seats = party.seats.map((candidate) => (candidate === seat ? { ...candidate, online: false, heldUntil: now + PARTY_SEAT_MS, map: "" } : candidate));
  const others = idsOf(seats.filter((candidate) => candidate.character !== character));
  const notices: PartyNotice[] = others.length > 0 ? [{ kind: "offline", about: character, to: others, invite: 0 }] : [];
  return changed({ party: { ...party, version: party.version + 1, seats }, invites: state.invites }, notices);
}

/**
 * Whether an offline leader's lead is due to pass (decisions 9 and 12): it went offline at least `PARTY_LEAD_GRACE_MS`
 * ago, which its seat's `heldUntil` dates, and another member is online to take it.
 *
 * @param party The party, or null.
 * @param now The time in milliseconds since the epoch.
 * @returns True when `passLead` would move the lead.
 */
export function leadDue(party: Party | null, now: number): boolean {
  const seat = seatOf(party, party?.leader ?? 0);
  if (party === null || seat === undefined || seat.online || seat.heldUntil - PARTY_SEAT_MS + PARTY_LEAD_GRACE_MS > now) {
    return false;
  }
  return party.seats.some((candidate) => candidate !== seat && candidate.online);
}

/**
 * Passes the lead of a leader gone longer than the grace (decisions 3, 9 and 12) to the online member who joined
 * earliest, with the leader's rights; the former leader keeps its seat without the right to invite, and its pending
 * invites are withdrawn. Every member is told `leader`. A returning former leader does not take it back.
 *
 * @param state The party's state.
 * @param now The time in milliseconds since the epoch.
 * @returns The change; nothing changes while `leadDue` is false.
 */
export function passLead(state: PartyState, now: number): Outcome {
  const party = state.party;
  if (party === null || !leadDue(party, now)) {
    return changed(state);
  }
  const former = party.leader;
  const leader = successor(party.seats.filter((candidate) => candidate.character !== former)).character;
  const seats = movedLead(party.seats, former, leader);
  const others = idsOf(seats.filter((candidate) => candidate.character !== former));
  const moved = changed({ party: { ...party, version: party.version + 1, leader, seats }, invites: state.invites }, [{ kind: "leader", about: leader, to: others, invite: 0 }]);
  return then(moved, withdraw(moved.state, former, "revoked", now));
}

/**
 * A member's character joined again (decision 12): its seat is its again whether or not the sweep has run, online at
 * the map and gateway it joined at; the others are told when it was offline. A leader back within the grace still
 * holds the lead; one whose lead passed does not regain it.
 *
 * @param state The party's state.
 * @param who The returning character.
 * @returns The change; nothing changes for a character not seated or already there.
 */
export function returning(state: PartyState, who: Who): Outcome {
  const party = state.party;
  const seat = seatOf(party, who.character);
  if (party === null || seat === undefined || (seat.online && seat.map === who.map && seat.gateway === who.gateway)) {
    return changed(state);
  }
  const seats = party.seats.map((candidate) => (candidate === seat ? { ...candidate, online: true, heldUntil: 0, map: who.map, gateway: who.gateway } : candidate));
  const notices: PartyNotice[] = seat.online ? [] : [{ kind: "online", about: who.character, to: idsOf(party.seats.filter((candidate) => candidate !== seat)), invite: 0 }];
  return changed({ party: { ...party, version: party.version + 1, seats }, invites: state.invites }, notices);
}

/**
 * Frees every kept seat whose time has come (decision 12), as by leaving with the notice `left`, the earliest first and
 * by place when equal; each writes a truce to the members seated at that moment, and a party that falls to one ends.
 *
 * @param state The party's state.
 * @param now The time in milliseconds since the epoch.
 * @returns The change.
 */
export function lapse(state: PartyState, now: number): Outcome {
  let result = changed(state);
  for (;;) {
    const party = result.state.party;
    const due = party === null ? [] : party.seats.filter((seat) => !seat.online && seat.heldUntil <= now).sort((a, b) => a.heldUntil - b.heldUntil || a.place - b.place);
    if (due.length === 0) {
      return result;
    }
    result = then(result, part(result.state, (due[0] as PartySeat).character, "left", now));
  }
}
